import { Box, FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { type EPLITrainerDevelopmentPlan, type EPLITrainerDevelopmentPlanAttendee } from '../../../models/EPLITrainerDevelopmentPlan'
import { Modal } from '../../customComponents/Modal'
import { EPLITrainerDevelopmentPlanAttendeeCompletionStatus } from '../../../models/EPLITrainerDevelopmentPlanAttendeeCompletionStatus'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { type AttendeeRequest } from '../../../models/requestObjects/priorityOne/epliTrainerDevelopmentPlanRequestObjects'
import { UpdateEPLITrainerDevelopmentPlan } from '../../../services/EPLITrainerDevelopmentPlanService'
import { type EPLITrainerStatus } from '../../../models/EPLITrainerStatus'

interface AttendeeTrackingForm {
  id?: number
  fullName: string
  isdId: number
  isAttendeeFromDistrict: boolean | null
  districtId?: number | null
  status: EPLITrainerStatus
  completionStatus: string | null
}

export interface EPLITrainerDevelopmentPlanTrackingFormValues {
  attendees: AttendeeTrackingForm[]
  actualAmount: string | number | null
}
interface Props {
  onClose: () => void
  onSave: () => void
  epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan
  implementationPhase: boolean
}
export const EPLITrainerDevelopmentPlanTrackingModal = (props: Props): JSX.Element => {
  const isUpdate: boolean = props.epliTrainerDevelopmentPlan.isUpdate ?? false

  const attendeeToForm = (attendee: EPLITrainerDevelopmentPlanAttendee): AttendeeTrackingForm => ({
    id: attendee.id,
    fullName: attendee.fullName,
    isdId: attendee.isd.id,
    isAttendeeFromDistrict: attendee.district != null,
    districtId: attendee.district?.id,
    status: attendee.status,
    completionStatus: attendee.completionStatus ?? null
  })

  const [formValues, setFormValues] = useState<EPLITrainerDevelopmentPlanTrackingFormValues>({
    attendees: props.epliTrainerDevelopmentPlan.attendees?.map(attendeeToForm) ?? [],
    actualAmount: props.epliTrainerDevelopmentPlan.actualBudgetAmount ?? null
  })
  const [submitted, setSubmitted] = useState<boolean>(false)

  const isValidNumber = (value: string | number | null): boolean => {
    if (value === null || value === '') return true
    return !isNaN(Number(value)) && Number(value) >= 0
  }

  const isValidCompletionStatus = (status: string | null): boolean => {
    if (status == null || status.trim() === '') return false
    return Object.values(EPLITrainerDevelopmentPlanAttendeeCompletionStatus).includes(status as EPLITrainerDevelopmentPlanAttendeeCompletionStatus)
  }

  const validateForm = (): { isValid: boolean, errorMessage?: string } => {
    if (formValues.attendees.length !== props.epliTrainerDevelopmentPlan.numberOfAttendees) {
      return { isValid: false, errorMessage: 'Attendee count does not match expected number' }
    }

    const invalidAttendee = formValues.attendees.find(a => !isValidCompletionStatus(a.completionStatus))
    if (invalidAttendee !== undefined) {
      return { isValid: false, errorMessage: 'Please select completion status for all attendees' }
    }

    if (!isValidNumber(formValues.actualAmount)) {
      return { isValid: false, errorMessage: 'Actual Amount must be greater than or equal to 0' }
    }

    return { isValid: true }
  }

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleAttendeeFieldUpdate = (index: number, e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    const updatedAttendees = [...formValues.attendees]
    updatedAttendees[index] = { ...updatedAttendees[index], [name]: value }
    setFormValues({ ...formValues, attendees: updatedAttendees })
  }

  const handleSubmit = async (): Promise<void> => {
    if (!isUpdate) {
      toast.error('This plan has no updates to track.')
      props.onClose()
      return
    }

    setSubmitted(true)
    const validation = validateForm()
    if (!validation.isValid) {
      toast.error(validation.errorMessage ?? 'Please fill out required fields')
      return
    }

    const cleanAttendees: AttendeeRequest[] = formValues.attendees.map(a => ({
      id: a.id,
      fullName: a.fullName,
      isAttendeeFromDistrict: a.isAttendeeFromDistrict ?? false,
      isdId: a.isdId,
      districtId: a.districtId,
      status: a.status,
      completionStatus: a.completionStatus
    }))

    const updatedPlan = {
      id: props.epliTrainerDevelopmentPlan.id,
      priorityId: props.epliTrainerDevelopmentPlan.priorityId,
      epliTrainingId: props.epliTrainerDevelopmentPlan.epliTraining.id,
      numberOfAttendees: props.epliTrainerDevelopmentPlan.numberOfAttendees,
      budgetRequest: props.epliTrainerDevelopmentPlan.budgetRequest,
      actualAmount: formValues.actualAmount !== null && formValues.actualAmount !== '' ? Number(formValues.actualAmount) : null,
      attendees: cleanAttendees,
      isUpdate: props.epliTrainerDevelopmentPlan.isUpdate,
      isImplementationPhase: props.implementationPhase
    }

    const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(updatedPlan)

    if (!success) {
      errors?.forEach((error) => toast.error(error))
      return
    }

    toast.success('EPLI Trainer Plan Tracked')
    props.onSave()
  }

  const renderTrackingFields = (): JSX.Element => {
    if (!isUpdate) {
      return <Typography variant='body1'>This plan has no updates to track.</Typography>
    }

    return (
      <Stack gap='1rem'>
        <Typography variant='h5'>Training</Typography>

        {/* Loop through attendees and show form for each attendee */}
        {formValues.attendees.map((attendee, index) => (
          <FormControl
            key={`${attendee.id ?? 0}-${index}`}
            variant='filled'
            required
            error={submitted && !isValidCompletionStatus(attendee.completionStatus)}
          >
            <FormLabel id={`attendeeCompletionStatusLabel-${index}`}>How much of the training did {attendee.fullName} complete?</FormLabel>
            <RadioGroup
              aria-labelledby={`attendeeCompletionStatusLabel-${index}`}
              value={attendee.completionStatus ?? ''}
              id={`completionStatus-${index}`}
              name='completionStatus'
              data-testid={`completionStatus-${index}`}
              onChange={(e) => { handleAttendeeFieldUpdate(index, e) }}
            >
              {Object.values(EPLITrainerDevelopmentPlanAttendeeCompletionStatus).map(completionStatus => (
                <FormControlLabel
                  data-testid={`completionStatusOption-${index}-${completionStatus}`}
                  key={completionStatus}
                  value={completionStatus}
                  control={<Radio />}
                  label={completionStatus}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 14
                    }
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        ))}
        {formValues.attendees.length === 0 && (
          <Typography variant='body1'>No attendees have been added to this plan.</Typography>
        )}

        <Typography variant='h5'>Budget</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant='body1'>
            How much funding was actually used?
            <Typography component='span' variant='caption' fontStyle='italic' color='var(--grey-500)'> Optional</Typography>
          </Typography>
          <TextField
            id='budget-request'
            sx={{ mt: '.75em', width: '250px' }}
            variant='filled'
            onChange={handleFieldUpdate}
            label={'Enter Amount'}
            data-testid='actualBudgetAmount'
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            name='actualAmount'
            error={submitted && !isValidNumber(formValues.actualAmount)}
            value={formValues.actualAmount ?? ''}
          />
        </Box>
      </Stack>
    )
  }

  return (
    <Modal
      title='EPLI Trainer Tracking'
      open={true}
      data-testid='epliTrainerDevelopmentPlanTrackingModal'
      width='480px'
      confirmButtonText='Save'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={handleSubmit}
      bodyContent={<Stack gap='1rem' m='24px 0'>
        {renderTrackingFields()}
      </Stack>}
    />
  )
}
